import { Routes, Route, useLocation } from 'react-router-dom'
import { lazy, Suspense, useEffect } from 'react'
import { sendLandingBeacon } from './lib/beacon'
import { Helmet } from 'react-helmet-async'
import App from './App'
import { PricingTiers } from './components/PricingTiers'
import GetAppPage from './pages/GetAppPage'
import { RouteErrorBoundary } from './components/RouteErrorBoundary'

const BlogIndexPage = lazy(() => import('./pages/BlogIndexPage'))
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'))
const HubPage = lazy(() => import('./pages/HubPage'))
const ContentPage = lazy(() => import('./pages/ContentPage'))
const SupportPage = lazy(() => import('./pages/SupportPage'))
const DeleteAccountPage = lazy(() => import('./pages/DeleteAccountPage'))

function PageFallback() {
  return (
    <div style={{ padding: '6rem 1rem', textAlign: 'center', color: '#888' }}>
      Loading…
    </div>
  )
}

export default function AppRouter() {
  const location = useLocation()
  // Aggregate-only pageview counter (cookieless; see lib/beacon.ts).
  useEffect(() => {
    sendLandingBeacon('pageview', { path: location.pathname })
  }, [location.pathname])
  return (
    <Suspense fallback={<PageFallback />}>
      <RouteErrorBoundary resetKey={`${location.pathname}${location.search}`}>
      <Routes location={location}>
        <Route path="/" element={<App />} />
        <Route path="/get" element={<GetAppPage />} />

        {/* Policy and support routes. Firebase serves equivalent standalone
            HTML on a direct request; these components keep client-side
            navigation complete and accessible. Deletion aliases share the
            canonical /delete-account document. */}
        <Route path="/support" element={<SupportPage />} />
        <Route path="/delete-account" element={<DeleteAccountPage />} />
        <Route path="/account-deletion" element={<DeleteAccountPage />} />
        <Route path="/data-deletion" element={<DeleteAccountPage />} />

        {/* Blog */}
        <Route path="/blog" element={<BlogIndexPage />} />
        <Route path="/blog/:slug" element={<BlogPostPage />} />

        {/* Hubs */}
        <Route path="/compare" element={<HubPage kind="compare" />} />
        <Route path="/alternatives" element={<HubPage kind="alternative" />} />
        <Route path="/use-cases" element={<HubPage kind="use-case" />} />
        <Route path="/features" element={<HubPage kind="feature" />} />
        <Route path="/how-to" element={<HubPage kind="how-to" />} />

        {/* Detail pages */}
        <Route path="/compare/:slug" element={<ContentPage kind="compare" />} />
        <Route path="/alternatives/:slug" element={<ContentPage kind="alternative" />} />
        <Route path="/use-cases/:slug" element={<ContentPage kind="use-case" />} />
        <Route path="/features/:slug" element={<ContentPage kind="feature" />} />
        <Route path="/how-to/:slug" element={<ContentPage kind="how-to" />} />

        {/* Standalone marketing pages backed by static markdown */}
        <Route path="/how-it-works" element={<ContentPage kind="static" fixedSlug="how-it-works" />} />
        <Route path="/camera-guide" element={<ContentPage kind="static" fixedSlug="camera-guide" />} />
        <Route path="/accuracy" element={<ContentPage kind="static" fixedSlug="accuracy" />} />
        <Route path="/pricing" element={<ContentPage kind="static" fixedSlug="pricing" preBody={<PricingTiers showHeader={false} />} />} />
        <Route path="/faq" element={<ContentPage kind="static" fixedSlug="faq" />} />
        <Route path="/changelog" element={<ContentPage kind="static" fixedSlug="changelog" />} />
        <Route path="/android" element={<ContentPage kind="static" fixedSlug="android" />} />
        <Route path="/ios" element={<ContentPage kind="static" fixedSlug="ios" />} />
        <Route path="/examples" element={<ContentPage kind="static" fixedSlug="examples" />} />
        <Route path="/sample-report" element={<ContentPage kind="static" fixedSlug="sample-report" />} />
        <Route path="/about" element={<ContentPage kind="static" fixedSlug="about" />} />
        <Route path="/founder" element={<ContentPage kind="static" fixedSlug="founder" />} />
        <Route path="/biomechanics" element={<ContentPage kind="static" fixedSlug="biomechanics" />} />
        <Route path="/sweden" element={<ContentPage kind="static" fixedSlug="sweden" />} />
        <Route path="/agents" element={<ContentPage kind="static" fixedSlug="agents" />} />

        {/* 404 — render a real not-found page rather than re-mounting the
            homepage. The catch-all used to fall back to <App />, which
            silently mounted a second Helmet block on every legitimate route
            (because React Router still evaluates the catch-all in some
            transition/lazy edge-cases) and produced duplicate <title>,
            <meta description>, and OG tags in the prerendered HTML. */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </RouteErrorBoundary>
    </Suspense>
  )
}

function NotFoundPage() {
  return (
    <>
      <Helmet>
        <title>Page not found: AceSense</title>
        <meta name="description" content="This AceSense page does not exist." />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <main style={{
        minHeight: '60vh',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '4rem 1rem',
        textAlign: 'center',
        color: '#f7f7f5',
        background: '#0a0b0c',
      }}>
        <h1 style={{ fontSize: '2.4rem', margin: '0 0 0.5rem' }}>Page not found</h1>
        <p style={{ color: 'rgba(255,255,255,0.7)', maxWidth: '40ch', margin: '0 0 2rem' }}>
          That URL didn't match anything we have. Try the homepage, the blog, or
          the comparison hub.
        </p>
        <div style={{ display: 'flex', gap: '0.75rem' }}>
          <a href="/" style={{
            background: '#ccff00', color: '#0a0b0c', padding: '0.7rem 1.3rem',
            borderRadius: '100px', textDecoration: 'none', fontWeight: 600,
          }}>Home</a>
          <a href="/blog" style={{
            color: '#ccff00', padding: '0.7rem 1.3rem',
            borderRadius: '100px', textDecoration: 'none',
          }}>Blog</a>
          <a href="/compare" style={{
            color: '#ccff00', padding: '0.7rem 1.3rem',
            borderRadius: '100px', textDecoration: 'none',
          }}>Compare</a>
        </div>
      </main>
    </>
  )
}
